import { FormControlProps, SxProps, TextField, IconButton, InputAdornment, InputLabelProps } from "@mui/material";
import { Event } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import React, { useState, useRef, useEffect } from "react";
import { IMask, IMaskInput } from "react-imask";
import dayjs from "dayjs";
import { de } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

type Size = FormControlProps["size"];
type YearFormat = "4-digit" | "2-digit";
type Margin = FormControlProps["margin"];

type DateFieldProps = {
  className?: string;
  label?: string;
  value: string;
  placeholder?: string;
  id?: string;
  error?: boolean;
  helperText?: string;
  size?: Size;
  margin?: Margin;
  disabled?: boolean;
  required?: boolean;
  yearFormat?: YearFormat;
  InputLabelProps?: Partial<InputLabelProps>;
  onChange: (value: string) => void;
  onBlur?: () => void;
  sx?: SxProps;
  maskLazy?: boolean;
  showFormatOnFocus?: boolean;
  minDate?: string;
};

interface DateMaskInputProps {
  onChange: (event: { target: { name?: string; value: string } }) => void;
  name?: string;
  yearFormat?: YearFormat;
  lazy?: boolean;
  [key: string]: any;
}

const DateMaskInput = React.forwardRef<HTMLInputElement, DateMaskInputProps>((props, ref) => {
  const { onChange, yearFormat, lazy, ...other } = props;

  // console.log("DateMaskInput yearFormat:", yearFormat);

  const yearBlock =
    yearFormat === "2-digit"
      ? {
          jj: {
            mask: IMask.MaskedRange,
            placeholderChar: "j",
            from: 0,
            to: 99,
            maxLength: 2,
          },
        }
      : {
          jjjj: {
            mask: IMask.MaskedRange,
            placeholderChar: "j",
            from: 1900,
            to: 9999,
            maxLength: 4,
          },
        };

  // single date block definition
  const singleDateBlocks = {
    tt: {
      mask: IMask.MaskedRange,
      placeholderChar: "t",
      from: 1,
      to: 31,
      maxLength: 2,
    },
    mm: {
      mask: IMask.MaskedRange,
      placeholderChar: "m",
      from: 1,
      to: 12,
      maxLength: 2,
    },
    ...yearBlock,
  };

  const dateMask = yearFormat === "2-digit" ? "tt.`mm.`jj" : "tt.`mm.`jjjj";

  // definition for a single date mask
  const singleDateMask = {
    mask: dateMask,
    blocks: singleDateBlocks,
  };

  return (
    <IMaskInput
      {...other}
      mask={singleDateMask.mask}
      blocks={singleDateMask.blocks}
      inputRef={ref}
      onAccept={(value: any) => {
        if (onChange) {
          onChange({ target: { name: props.name, value } });
        }
      }}
      lazy={lazy}
      autofix={true}
    />
  );
});

const MobiliDate: React.FC<DateFieldProps> = (props) => {
  const { className, label, placeholder, id, error, helperText, size, margin, sx, disabled, required, yearFormat = "2-digit", InputLabelProps, maskLazy = false } = props;

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);

  // Format value for display - convert from ISO string to dd.mm.yy or dd.mm.yyyy format
  const formatDisplayValue = (value: string) => {
    if (!value) return "";

    // ISO format (yyyy-mm-dd), convert to dd.mm.yy or dd.mm.yyyy
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const date = dayjs(value);
      if (date.isValid()) {
        const day = date.date().toString().padStart(2, "0");
        const month = (date.month() + 1).toString().padStart(2, "0");
        if (yearFormat === "2-digit") {
          const year = date.year().toString().slice(-2);
          return `${day}.${month}.${year}`;
        } else {
          const year = date.year().toString();
          return `${day}.${month}.${year}`;
        }
      }
    }

    return value;
  };

  // If maskLazy is true, IMask placeholder is always visible (lazy=false)
  // If maskLazy is false, use user-specified or default placeholder
  const finalPlaceholder = maskLazy ? undefined : (placeholder || (yearFormat === "4-digit" ? "tt.mm.jjjj" : "tt.mm.jj"));
  const displayValue = formatDisplayValue(props.value as string);

  const [inputValue, setInputValue] = useState(displayValue);
  const [isUserTyping, setIsUserTyping] = useState(false);

  // keep inputValue synced when props.value changes from external source
  useEffect(() => {
    if (!isUserTyping) {
      setInputValue(displayValue);
    }
  }, [displayValue, isUserTyping]);

  const convertToISOFormat = (dateString: string): string => {
    if (!dateString) return "";

    // Handle dd.mm.yy or dd.mm.yyyy format
    const parts = dateString.split(".");
    if (parts.length !== 3) return "";

    const [day, month, year] = parts;
    if (!day || !month || !year) return "";

    // Validate numeric values
    const dayNum = parseInt(day, 10);
    const monthNum = parseInt(month, 10);
    let yearNum = parseInt(year, 10);

    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum!)) return "";
    if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) return "";

    // expand 2-digit year to correct 
    if (year.length === 2) {
      const currentCentury = Math.floor(new Date().getFullYear() / 100) * 100;
      yearNum = currentCentury + yearNum;
    }

    // Create date and validate it
    const date = dayjs().year(yearNum).month(monthNum - 1).date(dayNum);
    if (date.isValid() && date.date() === dayNum && date.month() === monthNum - 1 && date.year() === yearNum) {
      return date.format('YYYY-MM-DD');
    }

    return "";
  };

  const handleMaskChange = (event: { target: { name?: string; value: string } }) => {
    const rawValue = event.target.value;
    setIsUserTyping(true);
    setInputValue(rawValue);

    // Try to convert to ISO format, but don't require it to be complete
    const isoDate = convertToISOFormat(rawValue);

    // Only call onChange if we have a valid complete date
    // This allows partial input without triggering onChange with invalid data
    if (isoDate) {
      props.onChange(isoDate);
    }
  };

  const handleBlur = () => {
    setIsUserTyping(false);

    // If input is empty (after trimming), clear the value and notify parent
    if (!inputValue || inputValue.trim() === "") {
      setInputValue("");
      props.onChange("");
      props.onBlur?.();
      return;
    }

    const isoDate = convertToISOFormat(inputValue);
    if (isoDate) {
      // valid complete date
      props.onChange(isoDate);
    } else {
      // if incomplete (not valid but partially typed), don’t revert
      const parts = inputValue.split(".").filter(Boolean);
      const isPartial = parts.length < 3 || parts.some((p) => p.length < 2);

      if (isPartial) {
        // keep what user typed, don’t reset
        setInputValue(inputValue);
      } else {
        // invalid complete date → revert
        if ("value" in props && typeof props.value === "string") {
          const currentDisplayValue = formatDisplayValue(props.value);
          setInputValue(currentDisplayValue);
        }
      }
    }

    props.onBlur?.();
  };

  const onAdornmentClick = () => {
    setOpenDatePicker(true);
  };

  const parseStringToDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    // Try parsing ISO format first (yyyy-mm-dd)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const date = dayjs(dateString);
      return date.isValid() ? date.toDate() : null;
    }

    // Try parsing dd.mm.yy or dd.mm.yyyy format
    if (/^\d{2}\.\d{2}\.(\d{2}|\d{4})$/.test(dateString)) {
      const [day, month, year] = dateString.split(".");
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      const dayNum = parseInt(day);
      if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) return null;
      const date = dayjs().year(yearNum).month(monthNum - 1).date(dayNum);
      return date.isValid() ? date.toDate() : null;
    }

    return null;
  };

  const formatDateFromPicker = (date: Date): string => {
    if (!dayjs(date).isValid()) return "";
    return dayjs(date).format('YYYY-MM-DD');
  };

  return (
    // <>
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <TextField
        ref={textFieldRef}
        type="text"
        className={className}
        label={label}
        placeholder={
          props.showFormatOnFocus && focused && !inputValue
          ? "tt.mm.jj"
          : finalPlaceholder
        }
        id={id}
        value={inputValue}
        onChange={handleMaskChange}
        error={error}
        helperText={helperText}
        size={size}
        margin={margin}
        disabled={disabled}
        required={required}
        onBlur={() => {
          setFocused(false);
          handleBlur();
        }}
        onFocus={() => setFocused(true)}
        InputLabelProps={InputLabelProps}
        sx={{
          width: 120,
          ".MuiInputBase-root": {
            height: 35,
            padding: "5px",
            margin: "0px",
            fontSize: "15px",
          },
          ".MuiInputBase-input": {
            padding: "0px",
            margin: "0px",
          },
          ...sx,
        }}
        InputProps={{
          inputComponent: DateMaskInput,
          inputProps: {
            yearFormat: yearFormat,
            lazy: !maskLazy,
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton sx={{ margin: 0, padding: 0 }} onClick={onAdornmentClick} disabled={disabled}>
                <Event sx={{ fontSize: 18, margin: 0, padding: 0 }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <DatePicker
        open={openDatePicker}
        onClose={() => setOpenDatePicker(false)}
        value={parseStringToDate(props.value)}
        minDate={props.minDate ? parseStringToDate(props.minDate) : undefined}
        disabled={disabled}
        onChange={(newValue) => {
          if (newValue) {
            const isoFormatted = dayjs(newValue).format('YYYY-MM-DD');
            const displayFormatted = formatDisplayValue(isoFormatted);

            // Update both the input display and the props value
            setInputValue(displayFormatted);
            props.onChange(isoFormatted);
          } else {
            setInputValue("");
            props.onChange("");
          }
          setOpenDatePicker(false);
        }}
        slotProps={{
          textField: {
            sx: { display: "none" },
          },
          popper: {
            anchorEl: textFieldRef.current,
            placement: "bottom-start",
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default MobiliDate;
